import { create } from 'zustand';

function handleDiscoveryEvent(eventType, data, state, { setLiveStats, setCrawledUrls }) {
  if (eventType === 'start') {
    setLiveStats((prev) => ({
      ...prev,
      [state]: {
        pagesVisited: 0,
        pagesWithElections: 0,
        topPages: [],
        issues: [],
        status: `Crawling ${data.url}...`,
      },
    }));
  } else if (eventType === 'url') {
    setCrawledUrls((prev) => ({
      ...prev,
      [state]: [...(prev[state] || []), data.url],
    }));
    setLiveStats((prev) => ({
      ...prev,
      [state]: { ...(prev[state] || {}), currentUrl: data.url },
    }));
  } else if (eventType === 'progress') {
    setLiveStats((prev) => ({
      ...prev,
      [state]: {
        ...(prev[state] || {}),
        pagesVisited: data.pagesVisited,
        pagesWithElections: data.pagesWithElections,
        status: data.status || 'Crawling...',
      },
    }));
  } else if (eventType === 'issue') {
    setLiveStats((prev) => ({
      ...prev,
      [state]: {
        ...(prev[state] || {}),
        issues: [...((prev[state] || {}).issues || []), data.message],
      },
    }));
  } else if (eventType === 'complete') {
    setLiveStats((prev) => ({ ...prev, [state]: data }));
  } else if (eventType === 'error') {
    setLiveStats((prev) => ({
      ...prev,
      [state]: { ...(prev[state] || {}), error: data.message },
    }));
  }
}

/**
 * Store for scraper discovery operations
 * Persists discovery progress across navigation and tab switches
 */
export const useDiscoveryStore = create((set, get) => ({
  // State
  selectedState: '',
  liveStats: {}, // Map of state → live stats (includes issues array)
  crawledUrls: {}, // Map of state → urls
  activeRequests: {}, // Map of state → AbortController
  crawlStartTimes: {}, // Map of state → timestamp
  tick: 0, // Re-render counter for elapsed time display

  // Actions
  setSelectedState: (state) => set({ selectedState: state }),
  setLiveStats: (fn) =>
    set((state) => ({ liveStats: typeof fn === 'function' ? fn(state.liveStats) : fn })),
  setCrawledUrls: (fn) =>
    set((state) => ({ crawledUrls: typeof fn === 'function' ? fn(state.crawledUrls) : fn })),
  setActiveRequests: (fn) =>
    set((state) => ({ activeRequests: typeof fn === 'function' ? fn(state.activeRequests) : fn })),
  setCrawlStartTimes: (fn) =>
    set((state) => ({
      crawlStartTimes: typeof fn === 'function' ? fn(state.crawlStartTimes) : fn,
    })),
  setTick: (tick) => set({ tick }),

  // Start discovery for a state
  startDiscovery: async (state, refetch) => {
    if (!state) return;

    const { setLiveStats, setCrawledUrls, setCrawlStartTimes, setActiveRequests } = get();

    setCrawledUrls((prev) => ({ ...prev, [state]: [] }));
    setCrawlStartTimes((prev) => ({ ...prev, [state]: Date.now() }));
    setLiveStats((prev) => ({
      ...prev,
      [state]: {
        pagesVisited: 0,
        pagesWithElections: 0,
        topPages: [],
        issues: [],
        status: 'Starting...',
      },
    }));

    const controller = new AbortController();
    setActiveRequests((prev) => ({ ...prev, [state]: controller }));

    try {
      const token = localStorage.getItem('accessToken');
      const headers = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`/api/scrapers/discovery/${state}`, {
        method: 'POST',
        headers,
        signal: controller.signal,
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const events = buffer.split('\n\n');
        buffer = events.pop() || '';

        for (const eventStr of events) {
          const lines = eventStr.split('\n').filter((l) => l.trim());
          let eventType = '',
            eventData = '';

          for (const line of lines) {
            if (line.startsWith('event: ')) {
              eventType = line.slice(7).trim();
            } else if (line.startsWith('data: ')) {
              eventData = line.slice(6).trim();
            }
          }

          if (!eventType || !eventData) continue;

          try {
            const data = JSON.parse(eventData);
            handleDiscoveryEvent(eventType, data, state, { setLiveStats, setCrawledUrls });
          } catch (e) {
            console.error('Failed to parse SSE event:', e);
          }
        }
      }

      if (refetch) await refetch();
    } catch (err) {
      if (err.name !== 'AbortError') {
        setLiveStats((prev) => ({
          ...prev,
          [state]: { ...(prev[state] || {}), error: err.message },
        }));
      }
    } finally {
      setActiveRequests((prev) => {
        const next = { ...prev };
        delete next[state];
        return next;
      });
    }
  },

  // Stop discovery for a state
  stopDiscovery: (state) => {
    const { activeRequests, setActiveRequests } = get();
    if (activeRequests[state]) {
      activeRequests[state].abort();
      setActiveRequests((prev) => {
        const next = { ...prev };
        delete next[state];
        return next;
      });
    }
  },

  // Clear a completed discovery
  clearDiscovery: (state) => {
    set((s) => ({
      liveStats: (() => {
        const next = { ...s.liveStats };
        delete next[state];
        return next;
      })(),
      crawledUrls: (() => {
        const next = { ...s.crawledUrls };
        delete next[state];
        return next;
      })(),
      crawlStartTimes: (() => {
        const next = { ...s.crawlStartTimes };
        delete next[state];
        return next;
      })(),
    }));
  },

  // Clear all discoveries
  clearAll: () =>
    set({
      selectedState: '',
      liveStats: {},
      crawledUrls: {},
      activeRequests: {},
      crawlStartTimes: {},
      tick: 0,
    }),
}));
